import React, { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useEditor } from '@craftjs/core';

import FormActionButtons from '@common/components/FormActionButtons/FormActionButtons';
import { PAGE_ROUTES } from '@common/constants';
import Notification, {
  SEVERITY_OPTIONS,
  useNotification,
} from '@common/components/Notification/Notification';
import { TEMPLATES_PATHS } from '@common/network/ApiPaths';
import {
  parseEachOnTbodyRecursive,
  isValidEmail,
  mapFailedEmails,
  isResponseOk,
  isResponseBarRequest,
  validateDomForSave,
  getErrorMessages,
  createHtmlStringWithConvertedDom,
  mapErrorMessages,
} from '@common/helpers/helpers';
import { patchByPathAndData, postByPathAndData } from '@services/newServices';
import PreviewDialog from '@pages/TemplateEditor/components/PreviewDialog/PreviewDialog';
import { useTemplateDetailsStore } from '@store/templateDetailsStore';

function BottomFormButtons({
  frameEditorRef,
  setSaveLoading,
  isCustomEmail,
  recipients,
  setRecipients,
  templateSubject,
  currentLanguage,
}) {
  const { query } = useEditor();

  const setInitState = useTemplateDetailsStore((s) => s.setInitState);
  const editor = useTemplateDetailsStore((s) => s.editor);

  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const navigate = useNavigate();

  const { t } = useTranslation();

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [showStatuses, showNotifications, handleClose] = useNotification();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onCancel = () => {
    setInitState();
    navigate(PAGE_ROUTES.TEMPLATES);
  };

  const checkRecipientEmails = (recipients) => {
    const failedEmails = [];
    recipients.forEach((recipient) => {
      const isValid = recipient?.email && isValidEmail(recipient.email);
      if (!isValid) {
        failedEmails.push({
          message: t('bad_email_format'),
          id: recipient.id,
        });
      }
    });

    return failedEmails;
  };

  const onSendEmail = async () => {
    try {
      setIsSubmitting(true);

      const failedEmails = checkRecipientEmails(recipients);

      if (failedEmails?.length) {
        setRecipients(mapFailedEmails(failedEmails));
      } else {
        const response = await postByPathAndData({
          path: TEMPLATES_PATHS.SEND_CUSTOM_EMAIL,
          data: {
            recipients,
            json_data: query?.serialize(),
            html_data: createHtmlStringWithConvertedDom(
              parseEachOnTbodyRecursive(frameEditorRef.current?.innerHTML),
            ),
            subject: templateSubject,
          },
        });
        if (isResponseOk(response)) {
          navigate(PAGE_ROUTES.EMAIL_SENT);
        } else {
          console.log('something went wrong!!!');
        }
      }
    } catch (err) {
      console.log('something went wrong: ', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSave = async ({ jsonData, HTMLdata }) => {
    try {
      setSaveLoading(true);
      const updatedValues = {
        subject: templateSubject || null,
        json_data: jsonData,
        data: createHtmlStringWithConvertedDom(
          parseEachOnTbodyRecursive(HTMLdata),
        ),
      };

      const res = await patchByPathAndData({
        path: TEMPLATES_PATHS.PATCH_BY_ID,
        pathVariables: { id },
        data: updatedValues,
        responseType: 'blob',
      });

      if (isResponseOk(res)) {
        showNotifications([
          {
            severity: SEVERITY_OPTIONS.SUCCESS,
            message: t('save_success'),
            show: true,
          },
        ]);
      }
    } catch (err) {
      console.log('err: ', err);
      if (isResponseBarRequest(err)) {
        showNotifications([
          {
            severity: SEVERITY_OPTIONS.ERROR,
            message: t('save_error'),
            show: true,
          },
        ]);
      }
    } finally {
      setSaveLoading(false);
    }
  };

  const onSaveTemplate = () => {
    const {
      invalidRows,
      invalidTableSource,
      invalidCardsSource,
      invalidPlaceholderSource,
    } = validateDomForSave(query);

    const editorsInBackground = editor.filter(
      (editorState) => editorState.language !== currentLanguage,
    );

    if (editorsInBackground.filter(({ isValid }) => !isValid)) {
      showNotifications([
        {
          severity: SEVERITY_OPTIONS.ERROR,
          message: 'nesto se desilo',
          show: true,
        },
      ]);

      return;
    }

    if (invalidRows?.length || invalidTableSource || invalidCardsSource) {
      const errorMessageKeys = getErrorMessages({
        invalidRows,
        invalidTableSource,
        invalidCardsSource,
        invalidPlaceholderSource,
      });

      const errorMessages = mapErrorMessages({ errorMessageKeys, t });

      showNotifications([
        {
          severity: SEVERITY_OPTIONS.ERROR,
          message: errorMessages,
          show: true,
        },
      ]);
    } else {
      onSave({
        jsonData: query?.serialize(),
        HTMLdata: frameEditorRef.current?.innerHTML,
      });
    }
  };

  const onPreview = () => {
    setIsPreviewOpen(true);
  };

  return (
    <>
      <FormActionButtons
        isSubmitting={isSubmitting}
        onSave={onSaveTemplate}
        onSend={onSendEmail}
        onCancel={onCancel}
        onPreview={onPreview}
        isCustomEmail={isCustomEmail}
      />

      <PreviewDialog
        title={`${t('email_subject')}: ${
          templateSubject ? templateSubject : t('no_subject')
        }`}
        fullScreen={false}
        src={frameEditorRef.current?.innerHTML}
        isOpen={isPreviewOpen}
        onCancel={() => setIsPreviewOpen(false)}
      />
      <Notification showStatuses={showStatuses} handleClose={handleClose} />
    </>
  );
}

export default BottomFormButtons;
